import { TokenInfo } from '../types';

// Interface pour les routeurs Jupiter
export interface JupiterRouter {
  id: string;
  name: string;
  icon?: string;
}

// Interface pour la réponse de l'API Jupiter Token v2
export interface JupiterTokenResponse {
  id: string;
  name: string;
  symbol: string;
  icon?: string;
  decimals: number;
  circSupply?: number;
  totalSupply?: number;
  tokenProgram?: string;
  firstPool?: {
    id: string;
    createdAt: string;
  };
  holderCount?: number;
  audit?: {
    mintAuthorityDisabled: boolean;
    freezeAuthorityDisabled: boolean;
    topHoldersPercentage: number;
  };
  organicScore?: number;
  organicScoreLabel?: string;
  isVerified?: boolean;
  cexes?: string[];
  tags?: string[];
  fdv?: number;
  mcap?: number;
  usdPrice?: number;
  priceBlockId?: number;
  liquidity?: number;
  stats5m?: TokenStats;
  stats1h?: TokenStats;
  stats6h?: TokenStats;
  stats24h?: TokenStats;
  ctLikes?: number;
  smartCtLikes?: number;
  updatedAt?: string;
}

export interface TokenStats {
  priceChange?: number;
  liquidityChange?: number;
  volumeChange?: number;
  buyVolume?: number;
  sellVolume?: number;
  buyOrganicVolume?: number;
  sellOrganicVolume?: number;
  numBuys?: number;
  numSells?: number;
  numTraders?: number;
  numOrganicBuyers?: number;
  numNetBuyers?: number;
}

// Services pour l'API Jupiter
const JUPITER_API_BASE = 'https://lite-api.jup.ag';

export class JupiterApiService {
  // Cache pour optimiser les performances
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static readonly CACHE_DURATION = 30000; // 30 secondes

  // Convertir la réponse Jupiter en TokenInfo
  private static convertJupiterTokenToTokenInfo(jupiterToken: JupiterTokenResponse): TokenInfo {
    return {
      address: jupiterToken.id,
      symbol: jupiterToken.symbol,
      name: jupiterToken.name,
      decimals: jupiterToken.decimals,
      logoURI: jupiterToken.icon || '',
      price: jupiterToken.usdPrice || 0,
      priceChange24h: jupiterToken.stats24h?.priceChange || 0,
      volume24h: (jupiterToken.stats24h?.buyVolume || 0) + (jupiterToken.stats24h?.sellVolume || 0),
      marketCap: jupiterToken.mcap || 0,
      circulatingSupply: jupiterToken.circSupply,
      totalSupply: jupiterToken.totalSupply,
      verified: jupiterToken.isVerified || false
    };
  }

  // Rechercher des tokens par symbole, nom ou mint address
  static async searchTokens(query: string): Promise<TokenInfo[]> {
    try {
      if (!query.trim()) return [];

      const cacheKey = `search_${query}`;
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }

      const response = await fetch(`${JUPITER_API_BASE}/tokens/v2/search?query=${encodeURIComponent(query)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const jupiterTokens: JupiterTokenResponse[] = await response.json();
      const tokenInfos = jupiterTokens.map(token => this.convertJupiterTokenToTokenInfo(token));

      // Mettre en cache
      this.cache.set(cacheKey, { data: tokenInfos, timestamp: Date.now() });

      return tokenInfos;
    } catch (error) {
      console.error('Erreur Jupiter API searchTokens:', error);
      // Fallback avec des données mock
      return this.getMockTokens().filter(token => 
        token.symbol.toLowerCase().includes(query.toLowerCase()) ||
        token.name.toLowerCase().includes(query.toLowerCase()) ||
        token.address.toLowerCase().includes(query.toLowerCase())
      );
    }
  }

  // Obtenir les prix en temps réel pour plusieurs tokens
  static async getTokenPrices(tokenAddresses: string[]): Promise<Record<string, TokenInfo>> {
    try {
      if (tokenAddresses.length === 0) return {};

      const pricesMap: Record<string, TokenInfo> = {};
      
      // Rechercher chaque token individuellement (car l'API search peut prendre plusieurs addresses)
      const searchQueries = tokenAddresses.join(',');
      const response = await fetch(`${JUPITER_API_BASE}/tokens/v2/search?query=${searchQueries}`);
      
      if (response.ok) {
        const jupiterTokens: JupiterTokenResponse[] = await response.json();
        
        for (const jupiterToken of jupiterTokens) {
          if (tokenAddresses.includes(jupiterToken.id)) {
            pricesMap[jupiterToken.id] = this.convertJupiterTokenToTokenInfo(jupiterToken);
          }
        }
      }
      
      // Si certains tokens ne sont pas trouvés, utiliser les données mock
      for (const address of tokenAddresses) {
        if (!pricesMap[address]) {
          const mockToken = this.getMockTokens().find((t: TokenInfo) => t.address === address);
          if (mockToken) {
            pricesMap[address] = mockToken;
          }
        }
      }
      
      return pricesMap;
    } catch (error) {
      console.error('Erreur Jupiter API getTokenPrices:', error);
      
      // Fallback complet avec les données mock
      const fallbackPrices: Record<string, TokenInfo> = {};
      tokenAddresses.forEach(address => {
        const mockToken = this.getMockTokens().find((t: TokenInfo) => t.address === address);
        if (mockToken) {
          fallbackPrices[address] = mockToken;
        }
      });
      
      return fallbackPrices;
    }
  }

  // Obtenir le prix d'un token spécifique
  static async getTokenPrice(tokenAddress: string): Promise<TokenInfo | null> {
    try {
      const cacheKey = `price_${tokenAddress}`;
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }

      const response = await fetch(`${JUPITER_API_BASE}/tokens/v2/search?query=${tokenAddress}`);
      
      if (response.ok) {
        const jupiterTokens: JupiterTokenResponse[] = await response.json();
        const targetToken = jupiterTokens.find(token => token.id === tokenAddress);
        
        if (targetToken) {
          const tokenInfo = this.convertJupiterTokenToTokenInfo(targetToken);
          this.cache.set(cacheKey, { data: tokenInfo, timestamp: Date.now() });
          return tokenInfo;
        }
      }

      // Fallback avec les données mock
      const mockToken = this.getMockTokens().find(token => token.address === tokenAddress);
      return mockToken || null;
    } catch (error) {
      console.error('Erreur Jupiter API getTokenPrice:', error);
      
      // Fallback avec les données mock
      const mockToken = this.getMockTokens().find(token => token.address === tokenAddress);
      return mockToken || null;
    }
  }

  // Obtenir les tokens populaires/trending
  static async getTrendingTokens(): Promise<TokenInfo[]> {
    try {
      const cacheKey = 'trending_tokens';
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }

      // Utiliser l'endpoint toptrending avec intervalle 24h
      const response = await fetch(`${JUPITER_API_BASE}/tokens/v2/toptrending/24h?limit=20`);
      
      if (response.ok) {
        const jupiterTokens: JupiterTokenResponse[] = await response.json();
        const tokenInfos = jupiterTokens.map(token => this.convertJupiterTokenToTokenInfo(token));
        
        // Mettre en cache
        this.cache.set(cacheKey, { data: tokenInfos, timestamp: Date.now() });
        
        return tokenInfos;
      }

      // Fallback vers toporganicscore si toptrending échoue
      const fallbackResponse = await fetch(`${JUPITER_API_BASE}/tokens/v2/toporganicscore/24h?limit=20`);
      if (fallbackResponse.ok) {
        const jupiterTokens: JupiterTokenResponse[] = await fallbackResponse.json();
        const tokenInfos = jupiterTokens.map(token => this.convertJupiterTokenToTokenInfo(token));
        return tokenInfos;
      }

      return this.getMockTokens().slice(0, 5);
    } catch (error) {
      console.error('Erreur Jupiter API getTrendingTokens:', error);
      return this.getMockTokens().slice(0, 5);
    }
  }

  // Obtenir les tokens récents
  static async getRecentTokens(limit: number = 30): Promise<TokenInfo[]> {
    try {
      const cacheKey = `recent_tokens_${limit}`;
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }

      const response = await fetch(`${JUPITER_API_BASE}/tokens/v2/recent?limit=${limit}`);
      
      if (response.ok) {
        const jupiterTokens: JupiterTokenResponse[] = await response.json();
        const tokenInfos = jupiterTokens.map(token => this.convertJupiterTokenToTokenInfo(token));
        
        // Mettre en cache
        this.cache.set(cacheKey, { data: tokenInfos, timestamp: Date.now() });
        
        return tokenInfos;
      }

      return this.getMockTokens();
    } catch (error) {
      console.error('Erreur Jupiter API getRecentTokens:', error);
      return this.getMockTokens();
    }
  }

  // Obtenir des tokens vérifiés
  static async getVerifiedTokens(): Promise<TokenInfo[]> {
    try {
      const cacheKey = 'verified_tokens';
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION * 2) { // Cache plus long pour les tokens vérifiés
        return cached.data;
      }

      const response = await fetch(`${JUPITER_API_BASE}/tokens/v2/tag?query=verified`);
      
      if (response.ok) {
        const jupiterTokens: JupiterTokenResponse[] = await response.json();
        const tokenInfos = jupiterTokens
          .map(token => this.convertJupiterTokenToTokenInfo(token))
          .slice(0, 50); // Limiter à 50 tokens vérifiés
        
        // Mettre en cache
        this.cache.set(cacheKey, { data: tokenInfos, timestamp: Date.now() });
        
        return tokenInfos;
      }

      return this.getMockTokens().filter((token: TokenInfo) => token.verified);
    } catch (error) {
      console.error('Erreur Jupiter API getVerifiedTokens:', error);
      return this.getMockTokens().filter((token: TokenInfo) => token.verified);
    }
  }

  // Obtenir les tokens les plus tradés
  static async getTopTradedTokens(interval: '5m' | '1h' | '6h' | '24h' = '24h', limit: number = 20): Promise<TokenInfo[]> {
    try {
      const cacheKey = `top_traded_${interval}_${limit}`;
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }

      const response = await fetch(`${JUPITER_API_BASE}/tokens/v2/toptraded/${interval}?limit=${limit}`);
      
      if (response.ok) {
        const jupiterTokens: JupiterTokenResponse[] = await response.json();
        const tokenInfos = jupiterTokens.map(token => this.convertJupiterTokenToTokenInfo(token));
        
        // Mettre en cache
        this.cache.set(cacheKey, { data: tokenInfos, timestamp: Date.now() });
        
        return tokenInfos;
      }

      return this.getMockTokens().slice(0, limit);
    } catch (error) {
      console.error('Erreur Jupiter API getTopTradedTokens:', error);
      return this.getMockTokens().slice(0, limit);
    }
  }

  // Obtenir la liste des routeurs disponibles pour Jupiter
  static async getAvailableRouters(): Promise<JupiterRouter[]> {
    try {
      // Jupiter v6 utilise automatiquement les meilleurs routes
      // Retourner une liste des principaux DEXs/AMMs supportés
      return [
        { id: 'raydium', name: 'Raydium', icon: '⚡' },
        { id: 'serum', name: 'Serum', icon: '🔄' },
        { id: 'orca', name: 'Orca', icon: '🐋' },
        { id: 'aldrin', name: 'Aldrin', icon: '💫' },
        { id: 'crema', name: 'Crema Finance', icon: '🍦' },
        { id: 'lifinity', name: 'Lifinity', icon: '♾️' },
        { id: 'marinade', name: 'Marinade Finance', icon: '🥒' },
        { id: 'mercurial', name: 'Mercurial Finance', icon: '☿️' },
        { id: 'saber', name: 'Saber', icon: '⚔️' },
        { id: 'stepn', name: 'Step Finance', icon: '�' }
      ];
    } catch (error) {
      console.error('Erreur Jupiter API getAvailableRouters:', error);
      return [
        { id: 'raydium', name: 'Raydium', icon: '⚡' },
        { id: 'orca', name: 'Orca', icon: '🐋' },
        { id: 'serum', name: 'Serum', icon: '🔄' }
      ];
    }
  }

  // Analyser le contenu d'un post pour détecter les mentions de tokens
  static extractTokenMentions(content: string): string[] {
    const tokenPattern = /\$([A-Z]{2,10})\b/g;
    const matches = content.match(tokenPattern);
    return matches ? matches.map(match => match.slice(1)) : [];
  }

  // Enrichir un post avec les informations des tokens mentionnés
  static async enrichPostWithTokens(post: any): Promise<any> {
    try {
      const tokenSymbols = this.extractTokenMentions(post.content);
      if (tokenSymbols.length === 0) return post;

      const tokenInfos: TokenInfo[] = [];
      for (const symbol of tokenSymbols) {
        const tokens = await this.searchTokens(symbol);
        if (tokens.length > 0) {
          tokenInfos.push(tokens[0]);
        }
      }

      return {
        ...post,
        mentionedTokens: tokenInfos
      };
    } catch (error) {
      console.error('Erreur enrichPostWithTokens:', error);
      return post;
    }
  }

  // Nettoyer le cache expiré
  static cleanExpiredCache(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.CACHE_DURATION * 2) { // Double du temps de cache pour la suppression
        this.cache.delete(key);
      }
    }
  }

  // Données mock pour fallback
  private static getMockTokens(): TokenInfo[] {
    return [
      {
        address: 'So11111111111111111111111111111111111111112',
        symbol: 'SOL',
        name: 'Wrapped SOL',
        decimals: 9,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
        price: 145.47,
        priceChange24h: 1.51,
        volume24h: 8579314176,
        marketCap: 77275352038,
        circulatingSupply: 531207433,
        totalSupply: 603724547,
        verified: true,
        socialLinks: {
          twitter: 'https://twitter.com/solana',
          website: 'https://solana.com'
        }
      },
      {
        address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
        symbol: 'USDC',
        name: 'USD Coin',
        decimals: 6,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
        price: 1.00,
        priceChange24h: 0.02,
        volume24h: 890000000,
        marketCap: 32000000000,
        verified: true
      },
      {
        address: 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN',
        symbol: 'JUP',
        name: 'Jupiter',
        decimals: 6,
        logoURI: 'https://static.jup.ag/jupiter-logo.png',
        price: 0.85,
        priceChange24h: 12.5,
        volume24h: 45000000,
        marketCap: 850000000,
        verified: true
      },
      {
        address: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263',
        symbol: 'BONK',
        name: 'Bonk',
        decimals: 5,
        logoURI: 'https://arweave.net/hQiPZOsRZXGXBJd_82PhVdlM_hACsT_q6wqwf5cSY7I',
        price: 0.000028,
        priceChange24h: 15.67,
        volume24h: 12500000,
        marketCap: 1800000000,
        verified: true
      },
      {
        address: 'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So',
        symbol: 'mSOL',
        name: 'Marinade staked SOL',
        decimals: 9,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So/logo.png',
        price: 158.45,
        priceChange24h: 1.82,
        volume24h: 8900000,
        marketCap: 890000000,
        verified: true
      }
    ];
  }
}
